/**
 * «Юниверситет» → подвкладка «Grades» (29.09, владелец: «субтаб grades, где оператор видит
 * свои грейды»). Грейд — на пару оператор×квест (operator_grades, выставляют ОО и ЕО), поэтому
 * здесь — по каждому квесту, по филиалам; показываем квесты, где есть грейд или свои игры.
 * Данные — /api/grades/my и /api/grades/my-played (свои; оператору — только отправленные
 * назначения, гейт черновика). По ТЗ «Юниверситета» НЕ показываем ставку ₽, пороги «до
 * следующего грейда» и акцепты — правила грейдов владелец ещё обсуждает.
 * Бейдж: 1→I … 7→VII, 8 → VII (gradeRoman, как в герое). Картинка бейджа — только у филиала
 * с учебным пакетом (badges/ пакета по подписанной ссылке), иначе — римская цифра.
 * 13.10 (ПР10 архива 1С, Р11): под грейдами — свёрнутый блок «За всё время в ЮниКвесте» (свои игры и деньги).
 */
import { useEffect, useMemo, useState } from "react";
import { gradeRoman } from "@ub/shared/uni";
import { api } from "../../../api/client";
import type { Branch, Quest } from "../../../api/types";
import { useUniMedia } from "./useUniMedia";
import { AllTimeBlock } from "../../../components/allTime/AllTimeBlock";
import u from "./uni.module.css";
import css from "./grades.module.css";

interface GradeRow { quest_id: string; grade: number | string }
interface PlayedRow { quest_id: string; n: number | string }
/** quest_id → грейд / число своих игр */
interface Mine { grade: Map<string, number>; played: Map<string, number> }
interface Row { id: string; name: string; grade: number; played: number }
interface Group { branch: Branch; rows: Row[] }

/** undefined — грузится; null — не удалось загрузить */
function useMine(nonce: number): Mine | null | undefined {
  const [mine, setMine] = useState<Mine | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setMine(undefined);
    Promise.all([
      api<{ results?: GradeRow[] }>("/api/grades/my"),
      api<{ results?: PlayedRow[] }>("/api/grades/my-played"),
    ])
      .then(([g, p]) => {
        if (!alive) return;
        setMine({
          grade: new Map((g.results ?? []).map((x) => [x.quest_id, Number(x.grade) || 0])),
          played: new Map((p.results ?? []).map((x) => [x.quest_id, Number(x.n) || 0])),
        });
      })
      .catch(() => { if (alive) setMine(null); });
    return () => { alive = false; };
  }, [nonce]);
  return mine;
}

export function MyGrades({ branches, quests }: { branches: Branch[]; quests: Quest[] }) {
  const [nonce, setNonce] = useState(0);
  const mine = useMine(nonce);

  /* Филиалы — в порядке ряда чипов, квесты — в порядке /api/quests (sort_order филиала). */
  const groups = useMemo<Group[]>(() => {
    if (!mine) return [];
    return branches
      .map((branch) => ({
        branch,
        rows: quests
          .filter((q) => q.branch_id === branch.id)
          .map((q) => ({ id: q.id, name: q.name?.trim() || q.short_name || q.id, grade: mine.grade.get(q.id) ?? 0, played: mine.played.get(q.id) ?? 0 }))
          .filter((r) => r.grade > 0 || r.played > 0),
      }))
      .filter((g) => g.rows.length > 0);
  }, [branches, quests, mine]);

  if (mine === undefined) return <div className={u.state}><p>Загрузка…</p></div>;
  if (mine === null) {
    return (
      <div className={u.state}>
        <p>Не удалось загрузить грейды</p>
        <button type="button" className={`${u.btn} ${u.ghost}`} onClick={() => setNonce((n) => n + 1)}>Повторить</button>
      </div>
    );
  }

  return (
    <div className={u.root} data-fb="grades" data-fb-name="Grades">
      <p className={css.note}>Грейд — по каждому квесту, его выставляют ОО и ЕО.</p>
      {groups.length === 0 ? (
        <div className={u.state}><p>Грейдов и игр пока нет</p></div>
      ) : groups.map((g) => {
        const pack = (g.branch.uni_pack ?? "").trim();
        return pack
          ? <PackCard key={g.branch.id} group={g} pack={pack} />
          : <BranchCard key={g.branch.id} group={g} />;
      })}
      <AllTimeBlock />
    </div>
  );
}

/** Филиал с учебным пакетом — бейджи из пакета (те же картинки, что в герое). */
function PackCard({ group, pack }: { group: Group; pack: string }) {
  const url = useUniMedia(pack);
  return <BranchCard group={group} badge={(roman) => url(`badges/grade-${roman}.webp`)} />;
}

function BranchCard({ group, badge }: { group: Group; badge?: (roman: string) => string | undefined }) {
  const total = group.rows.reduce((s, r) => s + r.played, 0);
  return (
    <section className={u.card} aria-label={`Грейды: ${group.branch.name}`} data-fb={`grades:${group.branch.id}`} data-fb-name={`Филиал «${group.branch.name}»`}>
      <h3 className={u.h}>{group.branch.name} <b>игр: {total}</b></h3>
      <ul className={css.list}>
        {group.rows.map((r) => {
          const roman = r.grade ? gradeRoman(r.grade) : null;
          const src = roman && badge ? badge(roman) : undefined;
          return (
            <li key={r.id} className={css.row} data-fb-part="квест">
              {src
                ? <img className={css.badge} src={src} alt="" width={40} height={40} decoding="async" />
                : <span className={`${css.tile} ${roman ? "" : css.tileNone}`} aria-hidden="true">{roman ?? "—"}</span>}
              <span className={css.main}>
                <b>{r.name}</b>
                <small>{roman ? `Грейд ${roman}` : "Грейд не присвоен"} · игр: {r.played}</small>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
